import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, MarkerF, InfoWindowF } from '@react-google-maps/api';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import { ArrowLeft, MapPin, CalendarIcon, Loader2, Package, Layers, Truck, X, Filter, Check } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;
const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];

const PARIS_75001 = { lat: 48.8606, lng: 2.3376 };
const FRANCE_CENTER = { lat: 46.6, lng: 2.4 };
const DEFAULT_ZOOM = 5.6;const MAP_STYLES = [
    { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.neighborhood', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.land_parcel', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    // Autoroutes : couleur légèrement plus foncée (sans modifier l'épaisseur)
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#cbd5e1' }] },
    { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#94a3b8' }] },
];

const STATUT_LABELS = {
    devis_a_creer: 'Devis à créer',
    a_envoyer: 'À envoyer',
    envoye: 'Envoyé',
    relance_1: 'Relance 1',
    relance_2: 'Relance 2',
    relance_3: 'Relance 3',
    interesse: 'Intéressé',
    accepte: 'Accepté',
    refuse: 'Refusé',
    sans_reponse: 'Sans réponse',
};

// Couleurs de statut identiques à DevisPage.js
const STATUT_COLORS = {
    devis_a_creer: 'bg-rose-100 text-rose-700 border-rose-200',
    a_envoyer: 'bg-amber-100 text-amber-700 border-amber-200',
    envoye: 'bg-blue-100 text-blue-700 border-blue-200',
    relance_1: 'bg-orange-100 text-orange-700 border-orange-200',
    relance_2: 'bg-orange-100 text-orange-700 border-orange-200',
    relance_3: 'bg-red-100 text-red-700 border-red-200',
    interesse: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    accepte: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    refuse: 'bg-red-100 text-red-700 border-red-200',
    sans_reponse: 'bg-slate-100 text-slate-600 border-slate-200',
};

function getDateStr(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
}

function formatFR(dateStr) {
    if (!dateStr) return '-';
    const s = String(dateStr).split('T')[0];
    if (s.length >= 10 && s[4] === '-') {
        return `${s.slice(8, 10)}-${s.slice(5, 7)}-${s.slice(0, 4)}`;
    }
    return s;
}

export default function CarteGroupagePage() {
    const navigate = useNavigate();
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: GOOGLE_MAPS_KEY,
        libraries: LIBRARIES,
    });

    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateDebut, setDateDebut] = useState(null);
    const [dateFin, setDateFin] = useState(null);
    const [appliedDates, setAppliedDates] = useState({ debut: null, fin: null });
    const [statutsSelected, setStatutsSelected] = useState([]);
    const [selected, setSelected] = useState(null);
    const [searchMarker, setSearchMarker] = useState(null);

    const today = useMemo(() => {
        const d = new Date(); d.setHours(0, 0, 0, 0); return d;
    }, []);

    const mapRef = useRef(null);
    const searchInputRef = useRef(null);
    const autocompleteRef = useRef(null);

    const fetchPoints = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (appliedDates.debut) params.date_debut = appliedDates.debut;
            if (appliedDates.fin) params.date_fin = appliedDates.fin;
            if (statutsSelected.length > 0) params.statut = statutsSelected.join(',');
            const { data } = await axios.get(`${API}/api/carte-groupage`, {
                params,
                withCredentials: true,
            });
            setPoints(data);
        } catch {
            toast.error('Erreur chargement carte groupage');
        } finally {
            setLoading(false);
        }
    }, [appliedDates, statutsSelected]);

    useEffect(() => {
        fetchPoints();
    }, [fetchPoints]);

    useEffect(() => {
        if (!isLoaded || !searchInputRef.current || autocompleteRef.current) return;
        const ac = new window.google.maps.places.Autocomplete(searchInputRef.current, {
            componentRestrictions: { country: 'fr' },
            fields: ['geometry', 'formatted_address'],
        });
        ac.addListener('place_changed', () => {
            const place = ac.getPlace();
            if (place.geometry) {
                const lat = place.geometry.location.lat();
                const lng = place.geometry.location.lng();
                // Calcul distance Haversine depuis 75001 Paris
                const R = 6371;
                const dLat = (lat - PARIS_75001.lat) * Math.PI / 180;
                const dLng = (lng - PARIS_75001.lng) * Math.PI / 180;
                const a = Math.sin(dLat/2)**2 + Math.cos(PARIS_75001.lat * Math.PI/180) * Math.cos(lat * Math.PI/180) * Math.sin(dLng/2)**2;
                const distance = Math.round(2 * R * Math.asin(Math.sqrt(a)) * 10) / 10;
                setSearchMarker({ lat, lng, address: place.formatted_address, distance });
            }
        });
        autocompleteRef.current = ac;
    }, [isLoaded]);

    useEffect(() => {
        // Vue fixe sur la France — on ne zoom pas sur les points
        if (!isLoaded || !mapRef.current) return;
        mapRef.current.setCenter(FRANCE_CENTER);
        mapRef.current.setZoom(DEFAULT_ZOOM);
    }, [points, isLoaded]);

    const stats = useMemo(() => {
        const chargement = points.filter((p) => p.type === 'chargement').length;
        const dechargement = points.filter((p) => p.type === 'dechargement').length;
        return { chargement, dechargement, total: points.length };
    }, [points]);

    const mapContainerStyle = { width: '100%', height: '100%' };

    if (!isLoaded) {
        return (
            <div className="flex items-center justify-center h-[80vh]">
                <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-64px)]" data-testid="carte-groupage-page">
            {/* Header */}
            <div className="shrink-0 p-3 md:p-4 border-b bg-white">
                <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => navigate('/')} data-testid="carte-groupage-back-btn">
                            <ArrowLeft size={16} className="mr-1" />
                            <span className="hidden sm:inline">Retour</span>
                        </Button>
                        <h1 className="text-lg md:text-xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="carte-groupage-title">
                            Carte groupage
                        </h1>
                    </div>
                </div>

                {/* Search bar + Date range */}
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <div className="relative flex-1 min-w-[220px]">
                        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#2e7cd6] z-10" />
                        <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Rechercher une adresse..."
                            className="w-full pl-10 pr-20 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2e7cd6]/40 focus:border-transparent"
                            data-testid="carte-groupage-search"
                        />
                        {searchMarker && (
                            <button
                                onClick={() => { setSearchMarker(null); if (searchInputRef.current) searchInputRef.current.value = ''; }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 hover:text-slate-600"
                                data-testid="carte-groupage-clear-search"
                            >
                                Effacer
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0 px-3 py-1 rounded-lg bg-slate-50 border border-slate-200">
                        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Du</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 text-xs bg-white w-[140px] justify-start" data-testid="carte-groupage-date-debut">
                                    <CalendarIcon size={14} className="mr-1.5 text-slate-400" />
                                    {dateDebut ? format(new Date(dateDebut + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr }) : 'Toutes'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[60]" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dateDebut ? new Date(dateDebut + 'T00:00:00') : undefined}
                                    onSelect={(d) => setDateDebut(d ? getDateStr(d) : null)}
                                    disabled={{ before: today }}
                                    locale={fr}
                                />
                                {dateDebut && (
                                    <div className="p-2 border-t flex justify-end">
                                        <Button size="sm" variant="ghost" onClick={() => setDateDebut(null)}>Réinitialiser</Button>
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                        <Label className="text-xs font-medium text-slate-600 whitespace-nowrap">Au</Label>
                        <Popover>
                            <PopoverTrigger asChild>
                                <Button variant="outline" size="sm" className="h-8 text-xs bg-white w-[140px] justify-start" data-testid="carte-groupage-date-fin">
                                    <CalendarIcon size={14} className="mr-1.5 text-slate-400" />
                                    {dateFin ? format(new Date(dateFin + 'T00:00:00'), 'dd/MM/yyyy', { locale: fr }) : 'Toutes'}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0 z-[60]" align="start">
                                <Calendar
                                    mode="single"
                                    selected={dateFin ? new Date(dateFin + 'T00:00:00') : undefined}
                                    onSelect={(d) => setDateFin(d ? getDateStr(d) : null)}
                                    disabled={{ before: today }}
                                    locale={fr}
                                />
                                {dateFin && (
                                    <div className="p-2 border-t flex justify-end">
                                        <Button size="sm" variant="ghost" onClick={() => setDateFin(null)}>Réinitialiser</Button>
                                    </div>
                                )}
                            </PopoverContent>
                        </Popover>
                        <Button
                            size="sm"
                            className="h-8 text-xs bg-[#2e7cd6] hover:bg-[#2568b8] text-white"
                            onClick={() => setAppliedDates({ debut: dateDebut, fin: dateFin })}
                            data-testid="carte-groupage-date-valider"
                        >
                            <Check size={14} className="mr-1" />
                            Valider
                        </Button>
                    </div>

                    {/* Filtre statut devis (multi-select) */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 text-xs bg-white min-w-[170px] justify-start shrink-0" data-testid="carte-groupage-statut-filter">
                                <Filter size={14} className="mr-1.5 text-slate-400" />
                                {statutsSelected.length === 0
                                    ? 'Tous les statuts'
                                    : statutsSelected.length === 1
                                        ? STATUT_LABELS[statutsSelected[0]] || statutsSelected[0]
                                        : `${statutsSelected.length} statuts`}
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[240px] p-2 z-[60]" align="start">
                            <div className="space-y-0.5">
                                {Object.entries(STATUT_LABELS).map(([key, label]) => {
                                    const isOn = statutsSelected.includes(key);
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => setStatutsSelected((prev) => isOn ? prev.filter((s) => s !== key) : [...prev, key])}
                                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs hover:bg-slate-100 transition-colors ${isOn ? 'bg-slate-50' : ''}`}
                                            data-testid={`carte-groupage-statut-opt-${key}`}
                                        >
                                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${isOn ? 'bg-[#2e7cd6] border-[#2e7cd6]' : 'border-slate-300 bg-white'}`}>
                                                {isOn && <Check size={12} className="text-white" />}
                                            </div>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${STATUT_COLORS[key]}`}>
                                                {label}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                            {statutsSelected.length > 0 && (
                                <div className="pt-2 mt-2 border-t border-slate-100 flex justify-end">
                                    <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => setStatutsSelected([])}>
                                        Réinitialiser
                                    </Button>
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            {/* Info bar (equivalent to date navigation) */}
            <div className="shrink-0 flex items-center gap-4 px-4 py-2.5 bg-blue-50 border-b border-blue-200 flex-wrap">
                <div className="flex items-center gap-2">
                    <Truck size={16} className="text-[#2e7cd6]" />
                    <p className="text-sm font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {stats.total} point{stats.total !== 1 ? 's' : ''} à plus de 200 km
                    </p>
                </div>
                <div className="flex items-center gap-3 text-xs">
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-green-600 border-2 border-white shadow" />
                        <span className="font-medium text-slate-700">Chargement ({stats.chargement})</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <span className="w-3 h-3 rounded-full bg-[#2e7cd6] border-2 border-white shadow" />
                        <span className="font-medium text-slate-700">Déchargement ({stats.dechargement})</span>
                    </div>
                </div>
                <div className="ml-auto text-xs text-slate-500">
                    Réf : 75001 Paris
                </div>
            </div>

            {/* Content: Map left + Liste devis right */}
            <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
                {/* Map */}
                <div className="flex-1 relative min-h-[300px]">
                    {loading && (
                        <div className="absolute inset-0 z-10 bg-white/60 flex items-center justify-center pointer-events-none">
                            <Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" />
                        </div>
                    )}
                    <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={FRANCE_CENTER}
                        zoom={DEFAULT_ZOOM}
                        onLoad={(map) => { mapRef.current = map; }}
                        options={{
                            streetViewControl: false,
                            mapTypeControl: false,
                            fullscreenControl: false,
                            zoomControl: true,
                            zoomControlOptions: { position: window.google.maps.ControlPosition.RIGHT_CENTER },
                            styles: MAP_STYLES,
                        }}
                    >
                        {/* Marker 75001 Paris masqué à la demande */}

                        {points.map((p, idx) => (
                            <MarkerF
                                key={`${p.devis_id}-${p.type}-${idx}`}
                                position={{ lat: p.lat, lng: p.lng }}
                                icon={{
                                    path: window.google.maps.SymbolPath.CIRCLE,
                                    fillColor: p.type === 'chargement' ? '#16a34a' : '#2e7cd6',
                                    fillOpacity: 1,
                                    strokeColor: '#fff',
                                    strokeWeight: 2,
                                    scale: 10,
                                }}
                                onClick={() => setSelected(p)}
                            />
                        ))}

                        {searchMarker && (
                            <MarkerF
                                position={{ lat: searchMarker.lat, lng: searchMarker.lng }}
                                icon={{
                                    url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="36" height="48" viewBox="0 0 36 48"><path d="M18 0C8.06 0 0 8.06 0 18c0 13.5 18 30 18 30s18-16.5 18-30C36 8.06 27.94 0 18 0z" fill="#f97316"/><circle cx="18" cy="18" r="7" fill="white"/></svg>'),
                                    scaledSize: new window.google.maps.Size(28, 37),
                                    anchor: new window.google.maps.Point(14, 37),
                                }}
                            />
                        )}

                        {selected && (
                            <InfoWindowF
                                position={{ lat: selected.lat, lng: selected.lng }}
                                onCloseClick={() => setSelected(null)}
                                options={{ pixelOffset: new window.google.maps.Size(0, -4), disableAutoPan: false }}
                            >
                                <div className="min-w-[230px] px-2.5 pt-2 pb-2.5" data-testid="carte-groupage-infowindow">
                                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-200">
                                        <div className="font-bold text-slate-900 text-sm truncate flex-1">{selected.client_nom}</div>
                                        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap shrink-0 ${STATUT_COLORS[selected.statut] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                            {STATUT_LABELS[selected.statut] || selected.statut}
                                        </span>
                                        <button
                                            onClick={() => setSelected(null)}
                                            className="shrink-0 p-0.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                                            data-testid="carte-groupage-infowindow-close"
                                            aria-label="Fermer"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                    <div className="space-y-1.5 text-xs text-slate-700">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`inline-block w-2 h-2 rounded-full ${selected.type === 'chargement' ? 'bg-green-600' : 'bg-[#2e7cd6]'}`} />
                                            <span className="font-semibold uppercase text-[10px] tracking-wider">
                                                {selected.type === 'chargement' ? 'Chargement' : 'Déchargement'}
                                            </span>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                            <CalendarIcon size={12} className="mt-0.5 text-slate-400 shrink-0" />
                                            <span><strong className="text-slate-900">Date :</strong> {formatFR(selected.date_demenagement)}</span>
                                        </div>
                                        <div className="flex items-start gap-1.5">
                                            <MapPin size={12} className="mt-0.5 text-slate-400 shrink-0" />
                                            <span>{selected.adresse}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Package size={12} className="text-slate-400 shrink-0" />
                                            <span><strong className="text-slate-900">Volume devis :</strong> {selected.volume_devis ? `${selected.volume_devis} m³` : '-'}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <Layers size={12} className="text-slate-400 shrink-0" />
                                            <span><strong className="text-slate-900">Distance :</strong> {selected.distance_km} km</span>
                                        </div>
                                    </div>
                                </div>
                            </InfoWindowF>
                        )}
                    </GoogleMap>

                    <div className="absolute bottom-4 left-4 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-md border border-slate-200">
                        <p className="text-xs font-medium text-slate-700" data-testid="carte-groupage-count">
                            {stats.total} point{stats.total !== 1 ? 's' : ''} affiché{stats.total !== 1 ? 's' : ''}
                        </p>
                    </div>

                    {/* Badge distance pour adresse recherchée */}
                    {searchMarker && (
                        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-white rounded-full px-4 py-2 shadow-lg border-2 border-orange-500 flex items-center gap-2" data-testid="carte-groupage-search-distance">
                            <MapPin size={14} className="text-orange-500" />
                            <span className="text-xs text-slate-600 truncate max-w-[260px]">{searchMarker.address}</span>
                            <span className="text-sm font-bold text-orange-600 whitespace-nowrap">· {searchMarker.distance} km</span>
                        </div>
                    )}
                </div>

                {/* Liste devis panel - right side */}
                <div className="w-full lg:w-[340px] border-t lg:border-t-0 lg:border-l border-slate-200 bg-gradient-to-b from-blue-50/50 to-white flex flex-col overflow-hidden" data-testid="carte-groupage-list-panel">
                    <div className="shrink-0 px-5 py-4 border-b border-blue-100 bg-blue-50/80">
                        <div className="flex items-center gap-2.5">
                            <div className="p-2 rounded-lg bg-blue-100">
                                <Truck size={18} className="text-[#2e7cd6]" />
                            </div>
                            <div>
                                <h2 className="text-sm font-bold text-slate-800">Devis groupage</h2>
                                <p className="text-xs text-slate-500">{stats.total} point{stats.total !== 1 ? 's' : ''} sur la carte</p>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {loading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 animate-spin text-[#2e7cd6]" />
                            </div>
                        ) : points.length === 0 ? (
                            <div className="px-5 py-10 text-center text-slate-400 text-xs">
                                Aucun devis avec une adresse à plus de 200 km de Paris dans cette période.
                            </div>
                        ) : (
                            <div className="p-3 space-y-2">
                                {points.map((p, idx) => (
                                    <button
                                        key={`item-${p.devis_id || p.inventaire_id}-${p.type}-${idx}`}
                                        onClick={() => {
                                            setSelected(p);
                                            if (mapRef.current) {
                                                mapRef.current.panTo({ lat: p.lat, lng: p.lng });
                                                mapRef.current.setZoom(9);
                                            }
                                        }}
                                        className={`w-full text-left px-3 py-2.5 rounded-lg border bg-white hover:shadow-md hover:border-[#2e7cd6] transition-all ${selected && (selected.devis_id || selected.inventaire_id) === (p.devis_id || p.inventaire_id) && selected.type === p.type ? 'border-[#2e7cd6] ring-2 ring-[#2e7cd6]/20' : 'border-slate-200'}`}
                                        data-testid={`carte-groupage-item-${idx}`}
                                    >
                                        <div className="flex items-center justify-between gap-2 mb-1">
                                            <span className="font-semibold text-sm text-slate-900 truncate">{p.client_nom}</span>
                                            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap ${STATUT_COLORS[p.statut] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                                                {STATUT_LABELS[p.statut] || p.statut}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1.5 text-[11px] text-slate-600 mb-1">
                                            <span className={`inline-block w-2 h-2 rounded-full ${p.type === 'chargement' ? 'bg-green-600' : 'bg-[#2e7cd6]'}`} />
                                            <span className="font-medium uppercase tracking-wider text-[10px]">
                                                {p.type === 'chargement' ? 'Chargement' : 'Déchargement'}
                                            </span>
                                            <span className="text-slate-400">·</span>
                                            <span>{formatFR(p.date_demenagement)}</span>
                                        </div>
                                        <p className="text-[11px] text-slate-500 truncate">{p.adresse}</p>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
